import paper from '@turbowarp/paper';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import bindAll from 'lodash.bindall';
import Modes from '../lib/modes';
import { MIXED } from '../helper/style-path';
import ColorStyleProptype from '../lib/color-style-proptype';
import GradientTypes from '../lib/gradient-types';

import { changeFillColor, clearFillGradient, DEFAULT_COLOR } from '../reducers/fill-style';
import { changeStrokeColor, clearStrokeGradient } from '../reducers/stroke-style';
import { changeMode } from '../reducers/modes';
import { clearHoveredItem, setHoveredItem } from '../reducers/hover';
import { clearSelectedItems, setSelectedItems } from '../reducers/selected-items';
import { setCursor } from '../reducers/cursor';
import { changeCornerRadius } from '../reducers/rounded-rect-mode';

import { clearSelection, getSelectedLeafItems } from '../helper/selection';
import RoundedRectTool from '../helper/tools/rounded-rect-tool';
import RoundedRectModeComponent from '../components/rounded-rect-mode/rounded-rect-mode.jsx';

class RoundedRectMode extends React.Component {
  constructor(props) {
    super(props);
    bindAll(this, ['activateTool', 'deactivateTool', 'validateColorState']);
  }
  componentDidMount() {
    if (this.props.isRoundedRectModeActive) {
      this.activateTool(this.props);
    }
  }
  componentWillReceiveProps(nextProps) {
    if (this.tool && nextProps.colorState !== this.props.colorState) {
      this.tool.setColorState(nextProps.colorState);
    }
    if (this.tool && nextProps.selectedItems !== this.props.selectedItems) {
      this.tool.onSelectionChanged(nextProps.selectedItems);
    }
    if (this.tool && nextProps.hoveredItemId !== this.props.hoveredItemId) {
      this.tool.setPrevHoveredItemId(nextProps.hoveredItemId);
    }
    if (this.tool && nextProps.cornerRadius !== this.props.cornerRadius) {
      this.tool.setCornerRadius(nextProps.cornerRadius);
    }
    if (this.tool && nextProps.proportional !== this.props.proportional) {
      this.tool.setProportional(nextProps.proportional);
    }

    if (nextProps.isRoundedRectModeActive && !this.props.isRoundedRectModeActive) {
      this.activateTool();
    } else if (!nextProps.isRoundedRectModeActive && this.props.isRoundedRectModeActive) {
      this.deactivateTool();
    }
  }
  componentDidUpdate(prevProps) {
    if (this.tool && this.props.proportional !== prevProps.proportional) {
      this.tool.setProportional(this.props.proportional);
    }
  }
  shouldComponentUpdate(nextProps) {
    return nextProps.isRoundedRectModeActive !== this.props.isRoundedRectModeActive;
  }
  componentWillUnmount() {
    if (this.tool) {
      this.deactivateTool();
    }
  }
  activateTool() {
    clearSelection(this.props.clearSelectedItems);
    this.validateColorState();

    this.tool = new RoundedRectTool(
      this.props.setHoveredItem,
      this.props.clearHoveredItem,
      this.props.setSelectedItems,
      this.props.clearSelectedItems,
      this.props.setCursor,
      this.props.onUpdateImage
    );
    this.tool.setColorState(this.props.colorState);
    this.tool.setCornerRadius(this.props.cornerRadius);
    this.tool.setProportional(this.props.proportional);
    this.tool.activate();
  }
  validateColorState() {
    // Make sure that at least one of fill/stroke is set, and that MIXED is not one of the colors.
    // If fill and stroke color are both missing, set fill to default and stroke to transparent.
    // If exactly one of fill or stroke color is set, set the other one to transparent.
    const { strokeWidth } = this.props.colorState;
    const fillColor1 = this.props.colorState.fillColor.primary;
    let fillColor2 = this.props.colorState.fillColor.secondary;
    let fillGradient = this.props.colorState.fillColor.gradientType;
    const strokeColor1 = this.props.colorState.strokeColor.primary;
    let strokeColor2 = this.props.colorState.strokeColor.secondary;
    let strokeGradient = this.props.colorState.strokeColor.gradientType;

    if (fillColor2 === MIXED) {
      this.props.clearFillGradient();
      fillColor2 = null;
      fillGradient = GradientTypes.SOLID;
    }
    if (strokeColor2 === MIXED) {
      this.props.clearStrokeGradient();
      strokeColor2 = null;
      strokeGradient = GradientTypes.SOLID;
    }

    const fillColorMissing =
      fillColor1 === MIXED ||
      (fillGradient === GradientTypes.SOLID && fillColor1 === null) ||
      (fillGradient !== GradientTypes.SOLID && fillColor1 === null && fillColor2 === null);
    const strokeColorMissing =
      strokeColor1 === MIXED ||
      strokeWidth === null ||
      strokeWidth === 0 ||
      (strokeGradient === GradientTypes.SOLID && strokeColor1 === null) ||
      (strokeGradient !== GradientTypes.SOLID && strokeColor1 === null && strokeColor2 === null);

    if (fillColorMissing && strokeColorMissing) {
      this.props.onChangeFillColor(DEFAULT_COLOR);
      this.props.clearFillGradient();
      this.props.onChangeStrokeColor(null);
      this.props.clearStrokeGradient();
    } else if (fillColorMissing && !strokeColorMissing) {
      this.props.onChangeFillColor(null);
      this.props.clearFillGradient();
    } else if (!fillColorMissing && strokeColorMissing) {
      this.props.onChangeStrokeColor(null);
      this.props.clearStrokeGradient();
    }
  }
  deactivateTool() {
    this.tool.deactivateTool();
    this.tool.remove();
    this.tool = null;
  }
  render() {
    return (
      <RoundedRectModeComponent
        isSelected={this.props.isRoundedRectModeActive}
        onMouseDown={this.props.handleMouseDown}
      />
    );
  }
}

RoundedRectMode.propTypes = {
  clearFillGradient: PropTypes.func.isRequired,
  clearStrokeGradient: PropTypes.func.isRequired,
  clearHoveredItem: PropTypes.func.isRequired,
  clearSelectedItems: PropTypes.func.isRequired,
  colorState: PropTypes.shape({
    fillColor: ColorStyleProptype,
    strokeColor: ColorStyleProptype,
    strokeWidth: PropTypes.number
  }).isRequired,
  cornerRadius: PropTypes.number.isRequired,
  handleMouseDown: PropTypes.func.isRequired,
  hoveredItemId: PropTypes.number,
  isRoundedRectModeActive: PropTypes.bool.isRequired,
  onChangeFillColor: PropTypes.func.isRequired,
  onChangeStrokeColor: PropTypes.func.isRequired,
  onUpdateImage: PropTypes.func.isRequired,
  proportional: PropTypes.bool,
  selectedItems: PropTypes.arrayOf(PropTypes.instanceOf(paper.Item)),
  setCursor: PropTypes.func.isRequired,
  setHoveredItem: PropTypes.func.isRequired,
  setSelectedItems: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  colorState: state.scratchPaint.color,
  isRoundedRectModeActive: state.scratchPaint.mode === Modes.ROUNDED_RECT,
  hoveredItemId: state.scratchPaint.hoveredItemId,
  proportional: state.scratchPaint.proportionalShape,
  selectedItems: state.scratchPaint.selectedItems,
  cornerRadius: state.scratchPaint.roundedRectMode.cornerRadius
});
const mapDispatchToProps = (dispatch) => ({
  setHoveredItem: (hoveredItemId) => {
    dispatch(setHoveredItem(hoveredItemId));
  },
  clearHoveredItem: () => {
    dispatch(clearHoveredItem());
  },
  clearSelectedItems: () => {
    dispatch(clearSelectedItems());
  },
  setSelectedItems: () => {
    dispatch(setSelectedItems(getSelectedLeafItems(), false /* bitmapMode */));
  },
  setCursor: (cursorString) => {
    dispatch(setCursor(cursorString));
  },
  handleMouseDown: () => {
    dispatch(changeMode(Modes.ROUNDED_RECT));
  },
  onChangeFillColor: (fillColor) => {
    dispatch(changeFillColor(fillColor));
  },
  onChangeStrokeColor: (strokeColor) => {
    dispatch(changeStrokeColor(strokeColor));
  },
  clearFillGradient: () => {
    dispatch(clearFillGradient());
  },
  clearStrokeGradient: () => {
    dispatch(clearStrokeGradient());
  },
  onChangeCornerRadius: (cornerRadius) => {
    dispatch(changeCornerRadius(cornerRadius));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(RoundedRectMode);
